import React, { useEffect, useState } from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  continueRender,
  delayRender,
  spring,
} from "remotion";
import { fetchAssetContent } from "../fetch-asset-content";
import type {
  CaptionEmphasis,
  CaptionEntrance,
  CaptionPage,
  CaptionsFile,
  CaptionStyle,
} from "./caption-style";
import {
  captionBackgroundStyle,
  captionSafeZone,
  captionTextEffectsStyle,
  captionTextStyle,
} from "./caption-style-css";
import { usePauseWhileLoading } from "./use-pause-while-loading";

export interface CaptionLayerProps {
  src: string;
  /**
   * The editor's unsaved style, merged on top of the fetched one so the
   * caption the player draws is the real one, moving. The editor doesn't have
   * to hide it and drag a stand-in, and the asset save cycle only publishes a
   * new `captions.json` once the gesture ends.
   */
  styleOverride?: Partial<CaptionStyle>;
  /** The editor's unsaved pages, on the same terms as `styleOverride`. */
  pagesOverride?: CaptionPage[];
  /**
   * Paints nothing while the editor holds its own copy of the strip. The layer
   * stays mounted and keeps its fetched document, so the caption comes back the
   * frame the editor closes instead of refetching and pausing the Player.
   */
  hiddenOverride?: boolean;
}

const ENTRANCE_DURATION_MS = 200;

/**
 * Remotion composition layer that renders word-level captions over video.
 * Supports layered entrance (how text appears) and emphasis (active word
 * styling) effects, plus backward-compatible single-animation mode.
 */
export function CaptionLayer({
  src: captionsFileUrl,
  styleOverride,
  pagesOverride,
  hiddenOverride,
}: CaptionLayerProps) {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const [captionsData, setCaptionsData] = useState<CaptionsFile | null>(null);
  const [handle] = useState(() => delayRender("Loading captions"));
  // Settled rather than `captionsData !== null`: a failed fetch has to release
  // the Player too, or playback hangs on a track that is never going to arrive.
  const [settled, setSettled] = useState(false);
  usePauseWhileLoading(!settled);

  // Load the captions data from the asset.
  useEffect(() => {
    let cancelled = false;

    fetchAssetContent(captionsFileUrl)
      .then((res) => res.json() as Promise<CaptionsFile>)
      .then((data) => {
        if (!cancelled) {
          setCaptionsData(data);
          setSettled(true);
          continueRender(handle);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load captions:", err);
          setSettled(true);
          continueRender(handle);
        }
      });

    return () => {
      cancelled = true;
      continueRender(handle);
    };
  }, [captionsFileUrl, handle]);

  if (hiddenOverride || !captionsData) {
    return null;
  }

  const captionStyle: CaptionStyle = styleOverride
    ? { ...captionsData.style, ...styleOverride }
    : captionsData.style;
  const pages = pagesOverride ?? captionsData.pages;
  if (pages.length === 0) {
    return null;
  }
  const currentTimeMs = (frame / fps) * 1000;

  const activePage = pages.find(
    (page) => currentTimeMs >= page.startMs && currentTimeMs < page.endMs,
  );

  if (!activePage) {
    return null;
  }

  const { entrance, emphasis } = resolveEffects(captionStyle);
  const activeTokenIndex = getActiveTokenIndex(
    activePage.tokens,
    currentTimeMs,
  );

  const tokenArgs = {
    tokens: activePage.tokens,
    emphasis,
    activeTokenIndex,
    currentTimeMs,
    baseColor: captionStyle.color,
    fps,
  };

  return (
    <AbsoluteFill>
      <div style={getPositionStyle(captionStyle, width, height)}>
        <span
          style={{
            position: "relative",
            ...captionTextStyle(captionStyle),
            // This span is a flex item, so it lays out as a block and the
            // background is one box around every wrapped line.
            ...captionBackgroundStyle(captionStyle),
            ...captionTextEffectsStyle(captionStyle),
          }}
        >
          {entrance.type === "typewriter"
            ? renderTypewriterTokens({
                ...tokenArgs,
                pageStartMs: activePage.startMs,
                pageEndMs: activePage.endMs,
                speed: entrance.speed ?? 0.7,
              })
            : renderTokens({ ...tokenArgs, entrance })}
        </span>
      </div>
    </AbsoluteFill>
  );
}

interface ResolvedEffects {
  entrance: CaptionEntrance;
  emphasis: CaptionEmphasis;
}

function resolveEffects(style: CaptionStyle): ResolvedEffects {
  if (style.entrance || style.emphasis) {
    return {
      entrance: style.entrance ?? { type: "none" },
      emphasis: style.emphasis ?? { type: "none" },
    };
  }
  const animation = style.animation ?? { type: "none" };
  if (animation.type === "highlight-word") {
    return {
      entrance: { type: "none" },
      emphasis: { type: "highlight-word", activeColor: animation.activeColor },
    };
  }
  return { entrance: { type: "none" }, emphasis: { type: "none" } };
}

interface TokenRenderArgs {
  tokens: CaptionPage["tokens"];
  emphasis: CaptionEmphasis;
  activeTokenIndex: number;
  currentTimeMs: number;
  baseColor: string;
  fps: number;
}

function renderTokens(
  args: TokenRenderArgs & { entrance: CaptionEntrance },
): React.ReactNode[] {
  const {
    tokens,
    entrance,
    emphasis,
    activeTokenIndex,
    currentTimeMs,
    baseColor,
    fps,
  } = args;
  return tokens.map((token, i) => {
    const spokenMs = currentTimeMs - token.timestampMs;
    const isActive = i === activeTokenIndex;
    const style = computeTokenStyle({
      entrance,
      emphasis,
      isActive,
      spokenMs,
      baseColor,
      fps,
    });
    return (
      <span key={i} style={style}>
        {token.token}
        {i < tokens.length - 1 ? " " : ""}
      </span>
    );
  });
}

function renderTypewriterTokens(
  args: TokenRenderArgs & {
    pageStartMs: number;
    pageEndMs: number;
    speed: number;
  },
): React.ReactNode[] {
  const {
    tokens,
    emphasis,
    activeTokenIndex,
    currentTimeMs,
    pageStartMs,
    pageEndMs,
    baseColor,
    fps,
    speed,
  } = args;
  const pageElapsedMs = currentTimeMs - pageStartMs;
  const pageDurationMs = pageEndMs - pageStartMs;

  const totalChars = tokens.reduce(
    (sum, t, i) => sum + t.token.length + (i < tokens.length - 1 ? 1 : 0),
    0,
  );

  // Reveal all characters within the first (1 - speed) of the page,
  // leaving the remaining portion for reading. speed=0.7 → reveal in first 30%.
  const revealDurationMs = Math.max(pageDurationMs * (1 - speed), 100);
  const progress = Math.min(1, pageElapsedMs / revealDurationMs);
  const totalRevealed = Math.floor(progress * totalChars);

  let charIndex = 0;
  const result: React.ReactNode[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]!;
    const tokenStart = charIndex;
    charIndex += token.token.length;
    const spaceStart = charIndex;
    if (i < tokens.length - 1) charIndex += 1;

    const revealedChars = Math.min(
      token.token.length,
      Math.max(0, totalRevealed - tokenStart),
    );
    if (revealedChars === 0) break;

    const isActive = i === activeTokenIndex;
    const spokenMs = currentTimeMs - token.timestampMs;
    const style: React.CSSProperties = { color: baseColor };
    applyEmphasis({ style, emphasis, isActive, spokenMs, fps });

    const displayText = token.token.slice(0, revealedChars);
    const showSpace = i < tokens.length - 1 && totalRevealed > spaceStart;

    result.push(
      <span key={i} style={style}>
        {displayText}
        {showSpace ? " " : ""}
      </span>,
    );
  }

  return result;
}

function computeTokenStyle(args: {
  entrance: CaptionEntrance;
  emphasis: CaptionEmphasis;
  isActive: boolean;
  spokenMs: number;
  baseColor: string;
  fps: number;
}): React.CSSProperties {
  const { entrance, emphasis, isActive, spokenMs, baseColor, fps } = args;
  const style: React.CSSProperties = { color: baseColor };

  switch (entrance.type) {
    case "fade-in": {
      if (spokenMs < 0) {
        style.opacity = 0;
      } else if (spokenMs < ENTRANCE_DURATION_MS) {
        style.opacity = easeOutCubic(spokenMs / ENTRANCE_DURATION_MS);
      }
      break;
    }
    case "slide-up": {
      const distance = entrance.distance ?? 15;
      style.display = "inline-block";
      if (spokenMs < 0) {
        style.opacity = 0;
        style.transform = `translateY(${distance}px)`;
      } else if (spokenMs < ENTRANCE_DURATION_MS) {
        const progress = easeOutCubic(spokenMs / ENTRANCE_DURATION_MS);
        style.opacity = progress;
        style.transform = `translateY(${distance * (1 - progress)}px)`;
      }
      break;
    }
  }

  applyEmphasis({ style, emphasis, isActive, spokenMs, fps });
  return style;
}

function applyEmphasis(args: {
  style: React.CSSProperties;
  emphasis: CaptionEmphasis;
  isActive: boolean;
  spokenMs: number;
  fps: number;
}): void {
  const { style, emphasis, isActive, spokenMs, fps } = args;

  switch (emphasis.type) {
    case "highlight-word":
      if (isActive) style.color = emphasis.activeColor;
      break;

    case "scale-pop": {
      const s = emphasis.scale ?? 1.2;
      if (isActive && spokenMs >= 0) {
        const localFrame = Math.max(0, Math.floor((spokenMs * fps) / 1000));
        const animation = spring({
          frame: localFrame,
          fps,
          durationInFrames: 4,
          config: { damping: 100 },
        });
        const scale = 1 + animation * (s - 1);
        style.display = "inline-block";
        style.verticalAlign = "middle";
        style.transform = appendTransform(style.transform, `scale(${scale})`);
      }
      break;
    }

    case "bounce": {
      if (spokenMs >= 0) {
        const localFrame = Math.max(0, Math.floor((spokenMs * fps) / 1000));
        const progress = spring({
          frame: localFrame,
          fps,
          config: { damping: 12, stiffness: 200, mass: 1 },
        });
        const distance = emphasis.distance ?? 8;
        const translateY = -distance * (1 - progress);
        if (Math.abs(translateY) > 0.1) {
          style.display = "inline-block";
          style.transform = appendTransform(
            style.transform,
            `translateY(${translateY}px)`,
          );
        }
      }
      break;
    }

    case "background-highlight":
      if (isActive) {
        style.backgroundColor = emphasis.activeColor;
        style.borderRadius = "4px";
        style.padding = "0 0.15em";
        style.margin = "0 0.05em";
      }
      break;
  }
}

function getActiveTokenIndex(
  tokens: CaptionPage["tokens"],
  currentTimeMs: number,
): number {
  return tokens.findLastIndex((t) => currentTimeMs >= t.timestampMs);
}

/**
 * Compute absolute CSS positioning for the caption container.
 *
 * Free-form `y` (composition pixels, vertical center) takes precedence and
 * keeps the legacy left/right safe-zone pair so the caption strip retains
 * the same effective width — anchoring to a single `left` would shrink the
 * usable width as the caption moves and re-wrap text. `style.x` is preserved
 * in the schema for backward compatibility but is no longer consulted.
 *
 * When `y` is unset, falls back to the legacy `position` enum, defaulting
 * to bottom placement.
 */
function getPositionStyle(
  style: CaptionStyle,
  videoWidth: number,
  videoHeight: number,
): React.CSSProperties {
  const zones = captionSafeZone(videoWidth, videoHeight);

  const horizontalPositioning: React.CSSProperties = {
    left: videoWidth * zones.sides,
    right: videoWidth * zones.sides,
  };

  if (style.y !== undefined) {
    return {
      position: "absolute",
      top: style.y,
      transform: "translateY(-50%)",
      display: "flex",
      justifyContent: "center",
      textAlign: "center",
      ...horizontalPositioning,
    };
  }

  let verticalPositioning: React.CSSProperties;
  switch (style.position) {
    case "top":
      verticalPositioning = { top: videoHeight * zones.top };
      break;
    case "center":
      verticalPositioning = { top: "50%", transform: "translateY(-50%)" };
      break;
    case "bottom":
    default:
      verticalPositioning = { bottom: videoHeight * zones.bottom };
      break;
  }

  return {
    position: "absolute",
    display: "flex",
    justifyContent: "center",
    textAlign: "center",
    ...horizontalPositioning,
    ...verticalPositioning,
  };
}

function appendTransform(
  existing: string | undefined,
  addition: string,
): string {
  return existing ? `${existing} ${addition}` : addition;
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}
